/**
 * Settings → Comment Generation (SPEC `Comment Generation` § Settings, #77-#81).
 *
 * Mixed save styles, the Scrapers-tab reasoning:
 *
 *  - **Backend** saves per action. The active picker writes immediately (#80) because binding a
 *    slot is one decision with one outcome, and each backend's config card owns its own Save (#78)
 *    and its own Test connection (#79) — a connection test has to run against what is actually
 *    stored, never against a pending draft. The cards are rendered from each backend's own
 *    `fields` through the shared `ConfigFieldsGrid`, so installing a backend needs no work here.
 *  - **Prompts** — the system prompt, the post-context template and the composer's presets — is one
 *    draft and one Save (#81). They are stored as one block, and a half-applied edit (new presets
 *    against the old context template) is a worse state than no edit.
 *
 * Nothing here is armed mid-edit, and a prompt change never reaches an existing chat: the context
 * template is rendered into the system prompt, and that is frozen, when a conversation starts
 * (SPEC §4).
 */

import { useEffect, useState } from 'react';
import {
  AI_MESSAGE_MAX_CHARS,
  AI_PRESET_NAME_MAX_CHARS,
  AI_PRESETS_MAX,
  AI_PROMPT_MAX_CHARS,
  type AiBackendDTO,
  type AiPreset,
  type AiTabDTO,
} from '@lw/shared';
import { describeError } from '../../api/client';
import { ErrorState, Spinner } from '../../components';
import { AI_ERROR_CLASS_LABEL } from '../../lib/labels';
import { ConfigFieldsGrid, configFromDraft, fieldErrorsOf, type ConfigDraft } from './configFields';
import {
  useAiQuery,
  useSetActiveAiBackend,
  useTestAiBackend,
  useUpdateAiBackendConfig,
  useUpdateAiPrompts,
} from './useAi';

/** A fresh row starts blank, which is also invalid — Save stays off until it is filled in. */
const NEW_PRESET: AiPreset = { name: '', prompt: '' };

/** Server-side substitution (SPEC § Supported variables); an unknown token renders as empty. */
const PROMPT_VARIABLES = [
  '{{POST_TEXT}}',
  '{{AUTHOR_NAME}}',
  '{{AUTHOR_HEADLINE}}',
  '{{POST_TYPE}}',
  '{{EMBEDDED_TEXT}}',
];

export function AiSection() {
  const query = useAiQuery();
  const data = query.data;

  if (query.isPending) return <Spinner size="lg" label="Loading comment generation…" />;
  if (query.isError) {
    return (
      <ErrorState
        title="Couldn't load comment generation"
        error={query.error}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  }
  if (data === undefined) return null;

  return (
    <>
      <ActiveBackendPicker data={data} />
      {data.backends.map((backend) => (
        <BackendCard key={backend.id} backend={backend} />
      ))}
      {data.backends.length === 0 ? (
        <p className="muted">
          No AI backends are installed. Add one under{' '}
          <code>packages/server/src/ai-backends/</code> and rebuild — see{' '}
          <code>AI_BACKENDS_README.md</code>.
        </p>
      ) : null}
      <PromptsBlock prompts={data.prompts} />
    </>
  );
}

function ActiveBackendPicker({ data }: { data: AiTabDTO }) {
  const setActive = useSetActiveAiBackend();

  return (
    <section className="settings-section">
      <h3 className="settings-title">Active backend</h3>
      <p className="field__hint">
        One backend serves every comment chat. Picking None disables the Generate comment action.
      </p>
      <div className="settings-grid">
        <div className="field">
          <label className="field__label" htmlFor="settings-ai-active">
            Backend
          </label>
          <select
            id="settings-ai-active"
            className="select"
            value={data.active ?? ''}
            disabled={setActive.isPending}
            onChange={(event) =>
              setActive.mutate(event.target.value === '' ? null : event.target.value)
            }
          >
            <option value="">None (disabled)</option>
            {data.backends.map((backend) => (
              <option key={backend.id} value={backend.id} disabled={!backend.configured}>
                {backend.name}
                {backend.configured ? '' : ' — not configured'}
              </option>
            ))}
          </select>
          {/* A finished sentence from the server, rendered verbatim (§12.5). */}
          {data.ready ? (
            <span className="settings-ok">Ready to generate.</span>
          ) : data.readyReason !== null ? (
            <span className="settings-bad">{data.readyReason}</span>
          ) : null}
        </div>
      </div>
      {setActive.isError ? (
        <p className="form-error" role="alert">
          {describeError(setActive.error)}
        </p>
      ) : null}
    </section>
  );
}

function BackendCard({ backend }: { backend: AiBackendDTO }) {
  const save = useUpdateAiBackendConfig();
  const test = useTestAiBackend();
  const [draft, setDraft] = useState<ConfigDraft>({});

  // A save answers with the fresh payload; drop the draft so the inputs show the stored truth.
  useEffect(() => {
    setDraft({});
  }, [backend]);

  const dirty = Object.keys(draft).length > 0;
  const fieldErrors = fieldErrorsOf(save.error);

  return (
    <section className="settings-section">
      <div className="scraper-head">
        <h3 className="settings-title">{backend.name}</h3>
        {backend.active ? (
          <span className="status status--ok">
            <span className="status__dot" aria-hidden="true" />
            Active
          </span>
        ) : null}
        {backend.configured ? null : (
          <span className="status status--queued">
            <span className="status__dot" aria-hidden="true" />
            Not configured
          </span>
        )}
        <span className="faint scraper-meta">
          <code>{backend.id}</code> · v{backend.version}
        </span>
      </div>
      <p className="muted">{backend.description}</p>

      <ConfigFieldsGrid
        idPrefix={`settings-ai-${backend.id}`}
        fields={backend.fields}
        draft={draft}
        onChange={(key, value) => setDraft({ ...draft, [key]: value })}
        errors={fieldErrors}
        onClearSecret={(key) => save.mutate({ backendId: backend.id, config: { [key]: '' } })}
        busy={save.isPending}
      />

      <div className="row settings-actions">
        <button
          type="button"
          className="btn-primary"
          disabled={!dirty || save.isPending}
          onClick={() => {
            if (!dirty) return;
            save.mutate({ backendId: backend.id, config: configFromDraft(backend.fields, draft) });
          }}
        >
          {save.isPending ? <Spinner size="sm" /> : null}
          Save
        </button>

        {backend.testable ? (
          <button
            type="button"
            className="table-action"
            onClick={() => test.mutate(backend.id)}
            disabled={test.isPending || !backend.configured}
          >
            {test.isPending ? <Spinner size="sm" /> : null}
            <span className="table-action__label">Test connection</span>
          </button>
        ) : null}

        {test.isSuccess ? (
          test.data.ok ? (
            <span className="settings-ok">Connected.</span>
          ) : (
            <span className="settings-bad">
              {AI_ERROR_CLASS_LABEL[test.data.errorClass] ?? test.data.errorClass}:{' '}
              {test.data.message}
            </span>
          )
        ) : null}
        {test.isError ? <span className="settings-bad">{describeError(test.error)}</span> : null}
      </div>

      {save.isError && Object.keys(fieldErrors).length === 0 ? (
        <p className="form-error" role="alert">
          {describeError(save.error)}
        </p>
      ) : null}
    </section>
  );
}

/**
 * The prompts *and* the presets: one draft, one Save (#81).
 *
 * They travel together because they are stored together and because a preset is only meaningful
 * against the context template it is answered with — a half-applied edit (new presets against the
 * old context) is a worse state than no edit. Dirtiness is a JSON compare rather than a field-by-
 * field one: the preset list changes shape, not just value.
 */
function PromptsBlock({ prompts }: { prompts: AiTabDTO['prompts'] }) {
  const save = useUpdateAiPrompts();
  const [draft, setDraft] = useState(prompts);

  // Re-seed whenever the stored block changes underneath the tab — a save answers with it, and so
  // does a background refetch.
  useEffect(() => {
    setDraft(prompts);
  }, [prompts]);

  const patchPreset = (index: number, patch: Partial<AiPreset>) => {
    setDraft({
      ...draft,
      presets: draft.presets.map((preset, at) => (at === index ? { ...preset, ...patch } : preset)),
    });
  };

  const dirty = JSON.stringify(draft) !== JSON.stringify(prompts);
  const valid =
    draft.systemPrompt.trim().length > 0 &&
    draft.contextTemplate.trim().length > 0 &&
    draft.presets.every(
      (preset) => preset.name.trim().length > 0 && preset.prompt.trim().length > 0,
    );

  return (
    <section className="settings-section">
      <h3 className="settings-title">Prompts</h3>
      <p className="field__hint">
        Edits apply to new chats only: a conversation freezes its system prompt — the rendered post
        context included — when it starts.
      </p>

      <div className="field">
        <label className="field__label" htmlFor="settings-ai-system">
          System prompt
        </label>
        <textarea
          id="settings-ai-system"
          className="input ai-prompt"
          rows={4}
          maxLength={AI_PROMPT_MAX_CHARS}
          value={draft.systemPrompt}
          onChange={(event) => setDraft({ ...draft, systemPrompt: event.target.value })}
        />
        <span className="field__hint">How the model should write — tone, length, house rules.</span>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="settings-ai-template">
          Post context template
        </label>
        <textarea
          id="settings-ai-template"
          className="input ai-prompt"
          rows={6}
          maxLength={AI_PROMPT_MAX_CHARS}
          value={draft.contextTemplate}
          onChange={(event) => setDraft({ ...draft, contextTemplate: event.target.value })}
        />
        <span className="field__hint">
          Rendered against the post and appended to the system prompt when a chat starts. The same
          variables work in preset prompts and in anything you type into a chat:{' '}
          {PROMPT_VARIABLES.map((variable, index) => (
            <span key={variable}>
              {index === 0 ? '' : ' · '}
              <code>{variable}</code>
            </span>
          ))}
          . An unknown <code>{'{{TOKEN}}'}</code> becomes an empty string.
        </span>
      </div>

      <div className="field">
        <span className="field__label">Presets</span>
        <span className="field__hint">
          One button each, in this order, under the chat composer. Pressing one sends its prompt —
          starting the chat, or continuing it.
        </span>
        <div className="ai-presets">
          {draft.presets.map((preset, index) => (
            <div className="ai-preset-row" key={index}>
              <input
                className="input"
                aria-label={`Preset ${String(index + 1)} name`}
                placeholder="Name"
                maxLength={AI_PRESET_NAME_MAX_CHARS}
                value={preset.name}
                onChange={(event) => patchPreset(index, { name: event.target.value })}
              />
              <textarea
                className="input"
                aria-label={`Preset ${String(index + 1)} prompt`}
                placeholder="What pressing it sends"
                rows={2}
                maxLength={AI_MESSAGE_MAX_CHARS}
                value={preset.prompt}
                onChange={(event) => patchPreset(index, { prompt: event.target.value })}
              />
              <button
                type="button"
                className="btn-ghost btn-ghost--sm"
                onClick={() =>
                  setDraft({ ...draft, presets: draft.presets.filter((_, at) => at !== index) })
                }
              >
                Remove
              </button>
            </div>
          ))}
        </div>
        <div className="row settings-actions">
          <button
            type="button"
            className="table-action"
            disabled={draft.presets.length >= AI_PRESETS_MAX}
            onClick={() => setDraft({ ...draft, presets: [...draft.presets, NEW_PRESET] })}
          >
            <span className="table-action__label">Add preset</span>
          </button>
          {draft.presets.length >= AI_PRESETS_MAX ? (
            <span className="muted">{AI_PRESETS_MAX} is the maximum.</span>
          ) : null}
        </div>
      </div>

      <div className="row settings-actions">
        <button
          type="button"
          className="btn-primary"
          disabled={!dirty || !valid || save.isPending}
          onClick={() =>
            save.mutate({
              systemPrompt: draft.systemPrompt,
              contextTemplate: draft.contextTemplate,
              presets: draft.presets,
            })
          }
        >
          {save.isPending ? <Spinner size="sm" /> : null}
          Save prompts
        </button>
        {!valid ? (
          <span className="settings-bad">
            Both prompts and every preset name and prompt are required.
          </span>
        ) : null}
        {save.isSuccess && !dirty ? <span className="settings-ok">Saved.</span> : null}
      </div>

      {save.isError ? (
        <p className="form-error" role="alert">
          {describeError(save.error)}
        </p>
      ) : null}
    </section>
  );
}

export default AiSection;
